import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import {
  ConnectedRelative,
  EventInvitation,
  EventJoinRequest,
  EventSummary,
  PublicEventSummary,
} from "src/app/models/screens/events.model";
import { RequestManagementUser } from "src/app/models/screens/requestManagement.model";
import {
  createEvent,
  getConnectedRelatives,
  getMyEvents,
  getParentChildren,
  getPublicEvents,
  getReceivedEventInvitations,
  getReceivedEventJoinRequests,
  requestToJoinPublicEvent,
  respondToEventInvitation,
  respondToEventJoinRequest,
  searchUsers,
} from "src/app/utils/backendEndpointCalls.util";

interface ParentChild {
  _id: string;
  firstName: string;
}
type CompanionPromptKind = "invitation" | "publicJoin";

interface CompanionPrompt {
  kind: CompanionPromptKind;
  invitationId?: string;
  eventId?: string;
  eventTitle: string;
  requireAtLeastOne: boolean;
  relativeLabel: string;
}

const EventsScreenViewModel = () => {
  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [userRole, setUserRole] = useState<string | undefined>(undefined);

  const [children, setChildren] = useState<ParentChild[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState<boolean>(false);
  const [adminUsers, setAdminUsers] = useState<RequestManagementUser[]>([]);
  const [isLoadingAdminUsers, setIsLoadingAdminUsers] =
    useState<boolean>(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("you");

  const [myEvents, setMyEvents] = useState<EventSummary[]>([]);
  const [isLoadingMyEvents, setIsLoadingMyEvents] = useState<boolean>(false);

  const [invitations, setInvitations] = useState<EventInvitation[]>([]);
  const [isLoadingInvitations, setIsLoadingInvitations] =
    useState<boolean>(false);
  const [respondingInvitationId, setRespondingInvitationId] = useState<
    string | null
  >(null);

  const [publicEvents, setPublicEvents] = useState<PublicEventSummary[]>([]);
  const [isLoadingPublicEvents, setIsLoadingPublicEvents] =
    useState<boolean>(false);
  const [requestingJoinEventId, setRequestingJoinEventId] = useState<
    string | null
  >(null);

  const [joinRequests, setJoinRequests] = useState<EventJoinRequest[]>([]);
  const [isLoadingJoinRequests, setIsLoadingJoinRequests] =
    useState<boolean>(false);
  const [respondingJoinRequestId, setRespondingJoinRequestId] = useState<
    string | null
  >(null);

  const [companionPrompt, setCompanionPrompt] =
    useState<CompanionPrompt | null>(null);
  const [companionRelatives, setCompanionRelatives] = useState<
    ConnectedRelative[]
  >([]);
  const [isLoadingCompanionRelatives, setIsLoadingCompanionRelatives] =
    useState<boolean>(false);
  const [selectedCompanionIds, setSelectedCompanionIds] = useState<string[]>(
    [],
  );
  const [isSubmittingCompanionPrompt, setIsSubmittingCompanionPrompt] =
    useState<boolean>(false);

  const [composeTitle, setComposeTitle] = useState<string>("");
  const [composeDescription, setComposeDescription] = useState<string>("");
  const [composeDate, setComposeDate] = useState<Dayjs | null>(dayjs());
  const [composeStartTime, setComposeStartTime] = useState<Dayjs | null>(null);
  const [composeDurationMinutes, setComposeDurationMinutes] =
    useState<string>("");
  const [composeLocation, setComposeLocation] = useState<string>("");
  const [composeIsPublic, setComposeIsPublic] = useState<boolean>(false);
  const [composeIsPaid, setComposeIsPaid] = useState<boolean>(false);
  const [composeFee, setComposeFee] = useState<string>("");
  const [composeBanner, setComposeBanner] = useState<File | null>(null);
  const [composeBannerPreviewUrl, setComposeBannerPreviewUrl] = useState<
    string | null
  >(null);
  const [isComposingEvent, setIsComposingEvent] = useState<boolean>(false);

  const getCurrentChildId = useCallback((): string | undefined => {
    if (userRole !== "Parent") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const showSuccess = (message: string) => {
    setIsSnackbarError(false);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const minutesToLabel = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const formatCurrency = (amount: number, currency: string): string => {
    if (currency === "GBP") return `£${amount.toFixed(2)}`;
    return `${amount.toFixed(2)} ${currency}`;
  };

  const actingUserIsModel = useMemo(() => {
    if (userRole === "Model") return true;
    if (userRole === "Parent" && selectedOwnerId !== "you") return true;
    return false;
  }, [userRole, selectedOwnerId]);

  const actingUserIsParent = useMemo(() => {
    if (userRole !== "Parent") return false;
    return selectedOwnerId === "you";
  }, [userRole, selectedOwnerId]);

  const shouldPromptForCompanions = actingUserIsModel || actingUserIsParent;

  const loadParentChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await getParentChildren();
    setIsLoadingChildren(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setChildren(response.children ?? []);
  }, []);

  const loadAdminUsers = useCallback(async () => {
    setIsLoadingAdminUsers(true);
    const response = await searchUsers("");
    setIsLoadingAdminUsers(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setAdminUsers(response.users ?? []);
  }, []);

  const loadMyEvents = useCallback(async () => {
    setIsLoadingMyEvents(true);
    const response = await getMyEvents(getCurrentChildId());
    setIsLoadingMyEvents(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setMyEvents(response.events ?? []);
  }, [getCurrentChildId]);

  const loadInvitations = useCallback(async () => {
    setIsLoadingInvitations(true);
    const response = await getReceivedEventInvitations(getCurrentChildId());
    setIsLoadingInvitations(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setInvitations(response.invitations ?? []);
  }, [getCurrentChildId]);

  const loadPublicEvents = useCallback(async () => {
    setIsLoadingPublicEvents(true);
    const response = await getPublicEvents(getCurrentChildId());
    setIsLoadingPublicEvents(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPublicEvents(response.events ?? []);
  }, [getCurrentChildId]);

  const loadJoinRequests = useCallback(async () => {
    setIsLoadingJoinRequests(true);
    const response = await getReceivedEventJoinRequests(getCurrentChildId());
    setIsLoadingJoinRequests(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setJoinRequests(response.joinRequests ?? []);
  }, [getCurrentChildId]);

  useEffect(() => {
    const savedRole = localStorage.getItem(localStorageConstants.ROLE);
    if (savedRole) setUserRole(savedRole);
  }, []);

  useEffect(() => {
    if (!userRole) return;
    if (userRole === "Parent") loadParentChildren();
    if (userRole === "Admin") loadAdminUsers();
  }, [loadAdminUsers, loadParentChildren, userRole]);

  useEffect(() => {
    if (!userRole) return;
    if (selectedTab === 0) loadMyEvents();
    if (selectedTab === 1) loadInvitations();
    if (selectedTab === 2) loadPublicEvents();
    if (selectedTab === 3) loadJoinRequests();
  }, [
    userRole,
    selectedTab,
    selectedOwnerId,
    loadMyEvents,
    loadInvitations,
    loadPublicEvents,
    loadJoinRequests,
  ]);

  useEffect(() => {
    return () => {
      if (composeBannerPreviewUrl) URL.revokeObjectURL(composeBannerPreviewUrl);
    };
  }, [composeBannerPreviewUrl]);

  const openCompanionPrompt = useCallback(
    async (prompt: CompanionPrompt) => {
      setCompanionPrompt(prompt);
      setSelectedCompanionIds([]);
      setIsLoadingCompanionRelatives(true);
      const response = await getConnectedRelatives(getCurrentChildId());
      setIsLoadingCompanionRelatives(false);
      if (!response.success) {
        showError(response.error);
        setCompanionPrompt(null);
        return;
      }
      setCompanionRelatives(response.relatives ?? []);
    },
    [getCurrentChildId],
  );

  const closeCompanionPrompt = () => {
    setCompanionPrompt(null);
    setCompanionRelatives([]);
    setSelectedCompanionIds([]);
  };

  const toggleCompanionSelection = (relativeId: string) => {
    setSelectedCompanionIds((prev) =>
      prev.includes(relativeId)
        ? prev.filter((id) => id !== relativeId)
        : [...prev, relativeId],
    );
  };

  const submitCompanionPrompt = async () => {
    if (!companionPrompt) return;
    if (
      companionPrompt.requireAtLeastOne &&
      selectedCompanionIds.length === 0
    ) {
      showError(
        `Please select at least one ${companionPrompt.relativeLabel.toLowerCase()}.`,
      );
      return;
    }
    setIsSubmittingCompanionPrompt(true);
    if (companionPrompt.kind === "invitation" && companionPrompt.invitationId) {
      const invitationId = companionPrompt.invitationId;
      setRespondingInvitationId(invitationId);
      const response = await respondToEventInvitation(
        invitationId,
        true,
        selectedCompanionIds,
        getCurrentChildId(),
      );
      setRespondingInvitationId(null);
      setIsSubmittingCompanionPrompt(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      setInvitations((prev) =>
        prev.map((inv) =>
          inv.id === invitationId ? { ...inv, status: "Accepted" } : inv,
        ),
      );
      showSuccess("Invitation accepted.");
      loadMyEvents();
      closeCompanionPrompt();
      return;
    }
    if (companionPrompt.kind === "publicJoin" && companionPrompt.eventId) {
      const eventId = companionPrompt.eventId;
      setRequestingJoinEventId(eventId);
      const response = await requestToJoinPublicEvent(
        eventId,
        selectedCompanionIds,
        getCurrentChildId(),
      );
      setRequestingJoinEventId(null);
      setIsSubmittingCompanionPrompt(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      setPublicEvents((prev) =>
        prev.map((e) =>
          e.id === eventId ? { ...e, hasPendingJoinRequest: true } : e,
        ),
      );
      showSuccess("Join request sent.");
      closeCompanionPrompt();
      return;
    }
    setIsSubmittingCompanionPrompt(false);
  };

  const onTabChanged = (_event: React.SyntheticEvent, value: number) => {
    setSelectedTab(value);
  };

  const onOwnerChanged = (ownerId: string) => {
    setSelectedOwnerId(ownerId);
    setMyEvents([]);
    setInvitations([]);
    setPublicEvents([]);
    setJoinRequests([]);
  };

  const onRespondInvitation = async (
    invitationId: string,
    accept: boolean,
    invitation?: EventInvitation,
  ) => {
    if (!accept) {
      setRespondingInvitationId(invitationId);
      const response = await respondToEventInvitation(
        invitationId,
        false,
        undefined,
        getCurrentChildId(),
      );
      setRespondingInvitationId(null);
      if (!response.success) {
        showError(response.error);
        return;
      }
      setInvitations((prev) =>
        prev.map((inv) =>
          inv.id === invitationId ? { ...inv, status: "Rejected" } : inv,
        ),
      );
      showSuccess("Invitation rejected.");
      return;
    }

    if (shouldPromptForCompanions) {
      const eventTitle =
        invitation?.event?.title ??
        invitations.find((inv) => inv.id === invitationId)?.event.title ??
        "this event";
      await openCompanionPrompt({
        kind: "invitation",
        invitationId,
        eventTitle,
        requireAtLeastOne: actingUserIsModel,
        relativeLabel: actingUserIsModel
          ? "Connected parents"
          : "Connected children",
      });
      return;
    }

    setRespondingInvitationId(invitationId);
    const response = await respondToEventInvitation(
      invitationId,
      true,
      undefined,
      getCurrentChildId(),
    );
    setRespondingInvitationId(null);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setInvitations((prev) =>
      prev.map((inv) =>
        inv.id === invitationId ? { ...inv, status: "Accepted" } : inv,
      ),
    );
    showSuccess("Invitation accepted.");
    loadMyEvents();
  };

  const onRequestJoinPublicEvent = async (
    eventId: string,
    event?: PublicEventSummary,
  ) => {
    if (shouldPromptForCompanions) {
      const eventTitle =
        event?.title ??
        publicEvents.find((e) => e.id === eventId)?.title ??
        "this event";
      await openCompanionPrompt({
        kind: "publicJoin",
        eventId,
        eventTitle,
        requireAtLeastOne: actingUserIsModel,
        relativeLabel: actingUserIsModel
          ? "Connected parents"
          : "Connected children",
      });
      return;
    }
    setRequestingJoinEventId(eventId);
    const response = await requestToJoinPublicEvent(
      eventId,
      undefined,
      getCurrentChildId(),
    );
    setRequestingJoinEventId(null);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPublicEvents((prev) =>
      prev.map((e) =>
        e.id === eventId ? { ...e, hasPendingJoinRequest: true } : e,
      ),
    );
    showSuccess("Join request sent.");
  };

  const onRespondJoinRequest = async (
    joinRequestId: string,
    accept: boolean,
  ) => {
    setRespondingJoinRequestId(joinRequestId);
    const response = await respondToEventJoinRequest(
      joinRequestId,
      accept,
      getCurrentChildId(),
    );
    setRespondingJoinRequestId(null);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setJoinRequests((prev) =>
      prev.map((r) =>
        r.id === joinRequestId
          ? { ...r, status: accept ? "Accepted" : "Rejected" }
          : r,
      ),
    );
    showSuccess(accept ? "Join request accepted." : "Join request rejected.");
  };

  const onComposeBannerSelected = (file: File | null) => {
    if (composeBannerPreviewUrl) {
      URL.revokeObjectURL(composeBannerPreviewUrl);
    }
    if (file && !file.type.startsWith("image/")) {
      showError("Banner must be an image file.");
      return;
    }
    setComposeBanner(file);
    setComposeBannerPreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  const resetComposeForm = () => {
    if (composeBannerPreviewUrl) {
      URL.revokeObjectURL(composeBannerPreviewUrl);
    }
    setComposeTitle("");
    setComposeDescription("");
    setComposeDate(dayjs());
    setComposeStartTime(null);
    setComposeDurationMinutes("");
    setComposeLocation("");
    setComposeIsPublic(false);
    setComposeIsPaid(false);
    setComposeFee("");
    setComposeBanner(null);
    setComposeBannerPreviewUrl(null);
  };

  const onSubmitComposeEvent = async () => {
    if (!composeTitle.trim()) {
      showError("Please enter a title.");
      return;
    }
    if (!composeDate) {
      showError("Please select a date.");
      return;
    }
    if (!composeStartTime || !composeStartTime.isValid()) {
      showError("Please select a valid start time.");
      return;
    }
    const fromNum = composeStartTime.hour() * 60 + composeStartTime.minute();
    const durationNum = parseInt(composeDurationMinutes, 10);
    if (Number.isNaN(durationNum) || durationNum <= 0) {
      showError("Please enter a valid duration in minutes.");
      return;
    }
    if (fromNum + durationNum > 24 * 60) {
      showError("Event end time must fall within the same day.");
      return;
    }
    if (!composeLocation.trim()) {
      showError("Please enter a location.");
      return;
    }
    let feeNum = 0;
    if (composeIsPaid) {
      feeNum = parseFloat(composeFee);
      if (Number.isNaN(feeNum) || feeNum <= 0) {
        showError(
          "Please enter a valid participation fee (greater than 0) for paid events.",
        );
        return;
      }
    }

    setIsComposingEvent(true);
    const response = await createEvent(
      {
        title: composeTitle.trim(),
        description: composeDescription.trim(),
        date: composeDate.format("YYYY-MM-DD"),
        from: fromNum,
        durationMinutes: durationNum,
        location: composeLocation.trim(),
        isPublic: composeIsPublic,
        isPaid: composeIsPaid,
        participationFee: feeNum,
      },
      composeBanner,
      getCurrentChildId(),
    );
    setIsComposingEvent(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    showSuccess("Event created successfully.");
    resetComposeForm();
    setSelectedTab(0);
  };

  return {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    selectedTab,
    onTabChanged,
    userRole,
    children,
    isLoadingChildren,
    adminUsers,
    isLoadingAdminUsers,
    selectedOwnerId,
    onOwnerChanged,
    myEvents,
    isLoadingMyEvents,
    invitations,
    isLoadingInvitations,
    onRespondInvitation,
    respondingInvitationId,
    publicEvents,
    isLoadingPublicEvents,
    onRequestJoinPublicEvent,
    requestingJoinEventId,
    joinRequests,
    isLoadingJoinRequests,
    onRespondJoinRequest,
    respondingJoinRequestId,
    companionPrompt,
    companionRelatives,
    isLoadingCompanionRelatives,
    selectedCompanionIds,
    isSubmittingCompanionPrompt,
    toggleCompanionSelection,
    submitCompanionPrompt,
    closeCompanionPrompt,
    composeTitle,
    setComposeTitle,
    composeDescription,
    setComposeDescription,
    composeDate,
    setComposeDate,
    composeStartTime,
    setComposeStartTime,
    composeDurationMinutes,
    setComposeDurationMinutes,
    composeLocation,
    setComposeLocation,
    composeIsPublic,
    setComposeIsPublic,
    composeIsPaid,
    setComposeIsPaid,
    composeFee,
    setComposeFee,
    composeBanner,
    composeBannerPreviewUrl,
    onComposeBannerSelected,
    isComposingEvent,
    onSubmitComposeEvent,
    minutesToLabel,
    formatCurrency,
  };
};

export default EventsScreenViewModel;
